/**
 * BulkEditPanel — the confirmation card for a proposed bulk edit.
 *
 * Rendered inside the agent sidebar's message feed when the bulk editor returns
 * a preview. This card IS the "ask the user before bulk editing" step: the
 * backend refuses to stage a preview that has not been approved through it, so
 * pressing Confirm here is the only way an agent-proposed change reaches the
 * grid.
 *
 * What the card has to get right, in priority order:
 *  1. The COUNT, unmissable. "42 rows" is the number that decides whether the
 *     user reads on or stops.
 *  2. The DISCLOSURES. A parent-scaled budget writes share x siblings; a Google
 *     Ads geo edit is campaign-scoped. These are rendered as a callout above the
 *     diff, not buried under it, because they are the cases where a
 *     reasonable-looking number is a 20x error.
 *  3. Blockers vs warnings. A blocker disables Confirm outright; a warning is
 *     shown and the user decides.
 *  4. The diff, collapsed past a handful of rows so a 200-row batch stays
 *     readable.
 *
 * Also renders the OPTIMIZATION agent's previews (`kind: 'optimization'`), which
 * share the protocol exactly — propose, disclose, confirm — and differ only in
 * what a change is (one optimizer suggestion rather than one cell delta) and in
 * consequence: confirming an optimization batch changes a LIVE campaign on the ad
 * platform with no Save step behind it. That difference is carried by
 * `savesImmediately` + the `live_platform_write` disclosure, not by a second
 * component, because a user who has learned to read this card should not have to
 * learn a different one for the higher-stakes case.
 *
 * ── Preserved proposals (GroundTruth) ──────────────────────────────────────
 * The GroundTruth bulk editor keeps every proposal card in the chat after it is
 * confirmed, cancelled, replaced or undone. It passes `status`, and the card
 * then shows what became of it — Applying… / Applied and saved / Save failed /
 * Cancelled / Undone / Replaced / Expired — in words and an icon (never colour
 * alone), in place of Confirm and Cancel. Everything else the card showed
 * (summary, count, disclosures, blockers, warnings, the row changes) stays.
 * `defaultCollapsed` folds the row-level diff behind a toggle for older cards.
 * Callers that pass neither get exactly the card they always had.
 */

import { useEffect, useRef, useState } from 'react';
import { PROPOSAL_STATUS_LABELS } from './gtChatState';

const PREVIEW_ROWS = 6;

/** Icon + tone per status. The label text carries the meaning; these only echo it. */
const STATUS_LOOK = {
  applying: { icon: '⟳', tone: 'progress' },
  staged: { icon: '⟳', tone: 'progress' },
  saved: { icon: '✓', tone: 'ok' },
  save_failed: { icon: '!', tone: 'fail' },
  cancelled: { icon: '✕', tone: 'muted' },
  undone: { icon: '↶', tone: 'muted' },
  superseded: { icon: '⇢', tone: 'muted' },
  expired: { icon: '–', tone: 'muted' },
  unconfirmed: { icon: '?', tone: 'warn' },
};

const fmtVal = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

export function BulkEditPanel({
  preview,
  onConfirm,
  onDismiss,
  approving = false,
  approveError = null,
  disabled = false,
  disabledReason = null,
  // True where confirming APPLIES AND PERSISTS in one step, rather than staging
  // pending edits for the user to save. The Copy Adjust grid works that way and
  // has no Save button, so promising one is a dead end. Defaults to the batched
  // behaviour, which is what the CLW review grid does.
  savesImmediately = false,
  // GroundTruth preserved proposals (see the header). `status` null/undefined
  // is the classic live card; 'pending' is a live card inside the GT history;
  // anything else is read-only history.
  status = null,
  statusDetail = null,
  defaultCollapsed = false,
}) {
  const [expanded, setExpanded] = useState(false);
  // Row-level details — folded by default on an older card, and folded again
  // when a newer proposal turns this one into an older card.
  const [detailsOpen, setDetailsOpen] = useState(!defaultCollapsed);
  useEffect(() => {
    if (defaultCollapsed) setDetailsOpen(false);
  }, [defaultCollapsed]);
  // A decided card drops its Confirm / Cancel — including the button that had
  // focus. Hand focus to the status line that replaced them, so a keyboard user
  // is not dropped at the top of the page.
  const cardRef = useRef(null);
  const statusRef = useRef(null);
  const liveBefore = useRef(false);
  const isReadOnly = status !== null && status !== undefined && status !== 'pending';
  useEffect(() => {
    if (liveBefore.current && isReadOnly) {
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      if (!active || active === document.body || cardRef.current?.contains(active)) {
        statusRef.current?.focus?.();
      }
    }
    liveBefore.current = status === 'pending';
  }, [status, isReadOnly]);
  if (!preview) return null;

  const {
    preview_id: previewId,
    kind,
    summary,
    affected_rows: affectedRows = 0,
    change_count: changeCount,
    changes = [],
    creates = [],
    duplicates = [],
    deletes = [],
    refused = [],
    warnings = [],
    blockers = [],
    disclosures = [],
  } = preview;

  const isRowOps = kind === 'row_ops';
  const isOptimization = kind === 'optimization';
  const hasBlockers = blockers.length > 0;
  const confirmDisabled = disabled || approving || hasBlockers;
  const tracked = status !== null && status !== undefined;
  const readOnly = isReadOnly;
  const look = readOnly ? (STATUS_LOOK[status] || { icon: '•', tone: 'muted' }) : null;
  const statusLabel = tracked ? (PROPOSAL_STATUS_LABELS[status] || status) : null;
  const detailCount = isRowOps
    ? creates.length + duplicates.length + deletes.length
    : changes.length;

  // Row-op batches describe entities, not cells, so the diff table is replaced
  // by three short lists.
  const shownChanges = expanded ? changes : changes.slice(0, PREVIEW_ROWS);
  const hiddenCount = Math.max(0, changes.length - shownChanges.length);

  const destructive = deletes.length > 0;
  // Computed inline rather than memoized: it is string concatenation, and the
  // arrays it reads are re-destructured off `preview` every render anyway, so a
  // memo would never hit. It also has to sit AFTER the `!preview` early return,
  // which a hook may not do — the hook count would change when a preview clears.
  const headline = (() => {
    if (isRowOps) {
      const parts = [];
      if (creates.length) parts.push(`${creates.reduce((a, c) => a + (c.count || 1), 0)} new`);
      if (duplicates.length) parts.push(`${duplicates.length} duplicated`);
      if (deletes.length) parts.push(`${deletes.length} deleted`);
      return parts.join(' · ') || 'No row changes';
    }
    const n = changeCount || changes.length;
    if (isOptimization) {
      return `${n} suggestion${n === 1 ? '' : 's'} to apply`;
    }
    return `${n} change${n === 1 ? '' : 's'} across ${affectedRows} row${affectedRows === 1 ? '' : 's'}`;
  })();

  const badge = isRowOps ? 'Row changes' : isOptimization ? 'Optimization' : 'Bulk edit';
  return (
    <div
      ref={cardRef}
      className={`clw-bulk-card ${destructive ? 'destructive' : ''} ${hasBlockers ? 'blocked' : ''}${readOnly ? ` readonly status-${status}` : ''}`}
      {...(tracked ? {
        role: 'group',
        'aria-label': `${badge} proposal: ${headline}${statusLabel ? ` — ${statusLabel}` : ''}`,
      } : {})}
    >
      <div className="clw-bulk-head">
        <span className="clw-bulk-badge">{badge}</span>
        <strong className="clw-bulk-count">{headline}</strong>
      </div>

      {/* What became of it — first, so a scrolled-past card is read correctly
          at a glance. Text + icon, so it reads without colour. */}
      {readOnly && (
        <div className={`clw-bulk-status ${look.tone}`} role="status" tabIndex={-1} ref={statusRef}>
          <span className="clw-bulk-status-icon" aria-hidden="true">{look.icon}</span>
          <strong className="clw-bulk-status-label">{statusLabel}</strong>
          {statusDetail && <span className="clw-bulk-status-detail">{statusDetail}</span>}
        </div>
      )}

      {summary && <div className="clw-bulk-summary">{summary}</div>}

      {/* Consequence callouts FIRST — these are the 20x-error cases. */}
      {disclosures.length > 0 && (
        <div className="clw-bulk-disclosures">
          {disclosures.map((d, i) => (
            <div key={i} className={`clw-bulk-disclosure ${d.blocking ? 'blocking' : ''}`}>
              <strong>
                {d.kind === 'campaign_scoped' ? 'Campaign-wide:'
                  : d.kind === 'live_platform_write' ? 'Changes the live campaign:'
                    : `Writes ${d.scope}:`}
              </strong>{' '}
              {d.detail}
              {d.example_row && d.kind === 'parent_scaled' && (
                <span className="clw-bulk-eg"> (e.g. {d.example_row})</span>
              )}
            </div>
          ))}
        </div>
      )}

      {hasBlockers && (
        <div className="clw-bulk-blockers">
          <strong>Can't apply this:</strong>
          <ul>
            {blockers.map((b, i) => <li key={i}>{b.message || b.code}</li>)}
          </ul>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="clw-bulk-warnings">
          <ul>
            {warnings.slice(0, 6).map((w, i) => (
              <li key={i}>{typeof w === 'string' ? w : `${w.row ? `${w.row}: ` : ''}${w.message}`}</li>
            ))}
            {warnings.length > 6 && <li>+{warnings.length - 6} more</li>}
          </ul>
        </div>
      )}

      {refused.length > 0 && (
        <div className="clw-bulk-refused">
          <strong>Skipped:</strong>
          <ul>
            {refused.slice(0, 4).map((r, i) => (
              <li key={i}>{r.field ? `${r.field} — ` : ''}{r.reason}</li>
            ))}
            {refused.length > 4 && <li>+{refused.length - 4} more</li>}
          </ul>
        </div>
      )}

      {(tracked || defaultCollapsed) && detailCount > 0 && (
        <button
          type="button"
          className="clw-bulk-more clw-bulk-details-toggle"
          aria-expanded={detailsOpen}
          onClick={() => setDetailsOpen((v) => !v)}
        >
          {detailsOpen
            ? 'Hide row details'
            : `Show row details (${detailCount} ${isRowOps ? 'row op' : 'change'}${detailCount === 1 ? '' : 's'})`}
        </button>
      )}

      {/* The diff. */}
      {!detailsOpen ? null : isRowOps ? (
        <div className="clw-bulk-rowops">
          {creates.map((c, i) => (
            <div key={`c${i}`} className="clw-bulk-rowop create">
              <span className="op">+ {c.count > 1 ? `${c.count}×` : ''}</span>
              <span className="lbl">clone of {c.template}</span>
              {Object.keys(c.overrides || {}).length > 0 && (
                <em className="ovr">
                  {Object.entries(c.overrides).map(([k, v]) => `${k}=${fmtVal(v)}`).join(', ')}
                </em>
              )}
            </div>
          ))}
          {duplicates.map((d, i) => (
            <div key={`d${i}`} className="clw-bulk-rowop duplicate">
              <span className="op">⧉</span><span className="lbl">{d.name || d.rowId}</span>
            </div>
          ))}
          {deletes.map((d, i) => (
            <div key={`x${i}`} className="clw-bulk-rowop delete">
              <span className="op">−</span><span className="lbl">{d.name || d.rowId}</span>
            </div>
          ))}
        </div>
      ) : (
        /* Two lines per change, not a five-column table.
           Real row names are long (`Audio_PMP_Lookalike_RT_LAL_All Users_NA_
           Website_Retargeting`), and five columns of that inside a ~500px panel
           cannot be laid out: a `<td>` ignores max-width under the default
           table-layout, so the name column grew, the table overflowed its card,
           and the name painted straight over the field and the before/after
           values. Giving the name its own full-width line and putting the delta
           underneath is robust at any name length and any panel width. */
        <div className="clw-bulk-diff">
          {shownChanges.map((c, i) => (
            <div key={i} className="clw-bulk-change">
              <div className="clw-bulk-change-row" title={c.name || c.rowId}>
                {c.name || c.rowId}
                {/* Optimization changes carry the optimizer's own priority. Shown
                    because it is the ranking the user is deciding against, and it
                    is not ours to re-derive. */}
                {c.priority && (
                  <span className={`clw-bulk-prio ${c.priority}`}>{c.priority}</span>
                )}
              </div>
              <div className="clw-bulk-change-delta">
                <span className="fld">{c.field}</span>
                <span className="before">{fmtVal(c.before)}</span>
                <span className="arrow">→</span>
                <span className="after">{fmtVal(c.after)}</span>
              </div>
              {/* The optimizer's reason. Only optimization previews set it; bulk
                  edits explain themselves in the agent's message above. */}
              {isOptimization && c.reason && (
                <div className="clw-bulk-change-why">{c.reason}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {detailsOpen && hiddenCount > 0 && (
        <button type="button" className="clw-bulk-more" {...(tracked ? { 'aria-expanded': false } : {})} onClick={() => setExpanded(true)}>
          Show {hiddenCount} more
        </button>
      )}
      {detailsOpen && expanded && changes.length > PREVIEW_ROWS && (
        <button type="button" className="clw-bulk-more" {...(tracked ? { 'aria-expanded': true } : {})} onClick={() => setExpanded(false)}>
          Show less
        </button>
      )}

      {/* A historical card is read-only: no Confirm, no Cancel, no stale
          approval error. Re-applying it means a new preview. */}
      {!readOnly && approveError && <div className="clw-bulk-error">{approveError}</div>}
      {!readOnly && disabled && disabledReason && <div className="clw-bulk-error">{disabledReason}</div>}

      {!readOnly && (
      <div className="clw-bulk-actions">
        <button
          type="button"
          className="clw-bulk-confirm"
          disabled={confirmDisabled}
          onClick={() => onConfirm?.(previewId)}
          title={hasBlockers
            ? "This can't be applied until the problems above are fixed"
            : isOptimization
              ? 'Send these changes to the live campaign on the ad platform'
              : savesImmediately
                ? 'Apply and save these changes'
                : 'Stage these changes in the grid — you still choose when to save'}
        >
          {approving
            ? 'Confirming…'
            : destructive ? `Confirm — delete ${deletes.length}`
              : isOptimization ? `Confirm — apply ${changes.length}`
                : 'Confirm'}
        </button>
        <button
          type="button"
          className="clw-bulk-cancel"
          onClick={() => onDismiss?.(previewId)}
          // GroundTruth: no Cancel racing an in-flight Confirm — the two would
          // leave the card saying Cancelled while the batch is applied.
          {...(tracked ? { disabled: approving } : {})}
        >
          Cancel
        </button>
        <span className="clw-bulk-note">
          {isOptimization
            ? 'Sent to the ad platform on confirm — there is no Save step.'
            : savesImmediately
              ? 'Applied and saved on confirm.'
              : 'Staged as pending edits — you save when ready.'}
        </span>
      </div>
      )}
    </div>
  );
}

export default BulkEditPanel;
